import { get, sortBy } from 'lodash';
import { storageKeys, storageTypes } from '../../../constants/globals';
import { getItem, removeItem, setItem } from '../../../utils/storage';

export const getCartUpsellSuggestionsStorageKey = (restaurantId) =>
  `${storageKeys.cartUpsellSuggestions}:${restaurantId || ''}`;

const readPersistedCartUpsellState = (restaurantId) => {
  if (!restaurantId) {
    return { suggestions: [], limit: 0, selectedOwnerParentIds: [] };
  }
  const persisted = getItem(
    storageTypes.sessionStorage,
    getCartUpsellSuggestionsStorageKey(restaurantId),
  );
  return {
    suggestions: get(persisted, 'suggestions', []),
    limit: get(persisted, 'limit', 0),
    selectedOwnerParentIds: get(persisted, 'selectedOwnerParentIds', []),
  };
};

const writePersistedCartUpsellState = (restaurantId, state) => {
  if (!restaurantId) {
    return;
  }
  setItem(storageTypes.sessionStorage, getCartUpsellSuggestionsStorageKey(restaurantId), state);
};

export const readPersistedCartUpsellSuggestions = (restaurantId) => {
  const { suggestions, limit } = readPersistedCartUpsellState(restaurantId);
  return { suggestions, limit };
};

export const readPersistedCartUpsellSelectedOwnerParentIds = (restaurantId) =>
  get(readPersistedCartUpsellState(restaurantId), 'selectedOwnerParentIds', []);

export const persistCartUpsellSuggestions = (restaurantId, suggestions, limit) => {
  if (!restaurantId || !Array.isArray(suggestions)) {
    return;
  }
  const prev = readPersistedCartUpsellState(restaurantId);
  writePersistedCartUpsellState(restaurantId, {
    ...prev,
    suggestions,
    limit: limit > 0 ? limit : suggestions.length,
  });
};

export const persistCartUpsellSelectedOwnerParentId = (
  restaurantId,
  ownerParentId,
  suggestionMeta = {},
) => {
  if (!restaurantId || !ownerParentId) {
    return;
  }
  const prev = readPersistedCartUpsellState(restaurantId);
  const selectedOwnerParentIds = Array.from(
    new Set([...(prev.selectedOwnerParentIds || []), ownerParentId]),
  );
  const suggestions = [...(prev.suggestions || [])];
  const existingIndex = suggestions.findIndex(
    (suggestion) => get(suggestion, 'suggestedOwnerParentId') === ownerParentId,
  );
  const nextSuggestion = {
    ...(existingIndex > -1 ? suggestions[existingIndex] : {}),
    suggestedOwnerParentId: ownerParentId,
    type: get(suggestionMeta, 'type', get(suggestions[existingIndex], 'type')),
    sortOrder: get(
      suggestionMeta,
      'sortOrder',
      get(suggestions[existingIndex], 'sortOrder', selectedOwnerParentIds.length),
    ),
  };
  if (existingIndex > -1) {
    suggestions[existingIndex] = nextSuggestion;
  } else {
    suggestions.push(nextSuggestion);
  }
  writePersistedCartUpsellState(restaurantId, {
    ...prev,
    suggestions,
    selectedOwnerParentIds,
    limit: prev.limit > 0 ? prev.limit : suggestions.length,
  });
};

export const removeCartUpsellSelectedOwnerParentId = (restaurantId, ownerParentId) => {
  if (!restaurantId || !ownerParentId) {
    return;
  }
  const prev = readPersistedCartUpsellState(restaurantId);
  const selectedOwnerParentIds = (prev.selectedOwnerParentIds || []).filter(
    (id) => id !== ownerParentId,
  );
  writePersistedCartUpsellState(restaurantId, {
    ...prev,
    selectedOwnerParentIds,
  });
};

export const clearPersistedCartUpsellSuggestions = (restaurantId) => {
  if (!restaurantId) {
    return;
  }
  removeItem(storageTypes.sessionStorage, getCartUpsellSuggestionsStorageKey(restaurantId));
};

export const filterCartUpsellSuggestionsInCart = (
  suggestions,
  cartLines,
  menuItemsByOwnerParentId,
) => {
  const inCartMenuItemIds = new Set(
    (cartLines || [])
      .map((line) => get(line, 'menuItemId', get(line, 'id')))
      .filter(Boolean),
  );

  return (suggestions || []).filter((suggestion) => {
    const ownerParentId = get(suggestion, 'suggestedOwnerParentId');
    if (!ownerParentId) {
      return false;
    }
    const menuItem = get(menuItemsByOwnerParentId, ownerParentId);
    return menuItem && inCartMenuItemIds.has(get(menuItem, 'id'));
  });
};

export const mergeCartUpsellSuggestions = (primary = [], secondary = []) => {
  const seen = new Set();
  const merged = [];

  (primary || []).forEach((suggestion) => {
    const ownerParentId = get(suggestion, 'suggestedOwnerParentId');
    if (!ownerParentId || seen.has(ownerParentId)) {
      return;
    }
    seen.add(ownerParentId);
    merged.push(suggestion);
  });

  sortBy(secondary || [], 'sortOrder').forEach((suggestion) => {
    const ownerParentId = get(suggestion, 'suggestedOwnerParentId');
    if (!ownerParentId || seen.has(ownerParentId)) {
      return;
    }
    seen.add(ownerParentId);
    merged.push(suggestion);
  });

  return merged;
};
